/* SystemVersionEditor — Studio CRUD for the monthly playbook.
   Simple Mode (default): 3 plain-language inputs + 1-click AI auto-generate.
   Advanced Mode: full JSON editor for power users.
   Non-technical owners only ever need the Simple Mode path. */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Plus,
    Sparkles,
    Trash2,
    RefreshCcw,
    Send,
    AlertCircle,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    Wand2,
    Eye,
} from "lucide-react";
import { studioHeaders } from "@/lib/studioAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function nextMonthString() {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const STYLE_PRESETS = [
    { value: "warm_film", label: "Warm Film", emoji: "🎞️", hint: "Kodak Portra · cream + amber + mocha · gentle grain" },
    { value: "minimal_nordic", label: "Minimal Nordic", emoji: "❄️", hint: "Bright daylight · off-white · negative space" },
    { value: "dramatic_dark", label: "Dramatic Dark", emoji: "🕯️", hint: "Single warm tungsten · deep blacks · cinematic" },
    { value: "custom", label: "Custom", emoji: "✍️", hint: "Describe your own style" },
];

export default function SystemVersionEditor() {
    const [versions, setVersions] = useState([]);
    const [activeMonth, setActiveMonth] = useState(null);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Simple-mode inputs (rehydrated from draft.simple_inputs when present)
    const [simpleTheme, setSimpleTheme] = useState("");
    const [simplePreset, setSimplePreset] = useState("warm_film");
    const [simpleNote, setSimpleNote] = useState("");
    const [simpleCustomStyle, setSimpleCustomStyle] = useState("");

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/system-version`, { headers: studioHeaders() });
            setVersions(r.data || []);
            if (!activeMonth && r.data?.[0]) setActiveMonth(r.data[0].month);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load versions");
        }
    }, [activeMonth]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!activeMonth) { setDraft(null); return; }
        (async () => {
            try {
                const r = await axios.get(`${API}/system-version/${activeMonth}`, { headers: studioHeaders() });
                setDraft(r.data);
                // Rehydrate the simple-mode inputs from saved record
                const si = r.data?.simple_inputs || {};
                setSimpleTheme(si.monthly_theme || "");
                setSimplePreset(si.visual_style_preset || "warm_film");
                setSimpleNote(si.client_note || r.data?.changelog || "");
                setSimpleCustomStyle(si.custom_style_note || "");
                setShowAdvanced(false);
            } catch {
                setDraft(null);
            }
        })();
    }, [activeMonth, versions]);

    const createMonth = async (month) => {
        try {
            setBusy(true);
            await axios.post(`${API}/system-version`, { month }, { headers: studioHeaders() });
            toast.success(`Created ${month} — fill in 3 things below and click Auto-Generate`);
            setActiveMonth(month);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Create failed");
        } finally { setBusy(false); }
    };

    const save = async () => {
        if (!draft) return;
        try {
            setBusy(true);
            await axios.put(`${API}/system-version/${draft.month}`, draft, { headers: studioHeaders() });
            toast.success("Saved");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    const publish = async () => {
        if (!draft) return;
        if (!window.confirm(`Publish ${draft.month}? Subscribers will see this content immediately.`)) return;
        try {
            setBusy(true);
            await axios.post(`${API}/system-version/${draft.month}/publish`, {}, { headers: studioHeaders() });
            toast.success(`${draft.month} published — live on your site now`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Publish failed");
        } finally { setBusy(false); }
    };

    const autoGenerate = async () => {
        if (!draft) return;
        if (!simpleTheme || simpleTheme.trim().length < 2) {
            toast.error("Please enter this month's theme first");
            return;
        }
        if (simplePreset === "custom" && !simpleCustomStyle.trim()) {
            toast.error("Custom style — please describe your visual style");
            return;
        }
        const ok = window.confirm(
            "Auto-generate the full month's content?\n\n" +
            "This will overwrite the current trends, hooks, styles, and prompts using AI. " +
            "Your 3 inputs are saved so you can re-run anytime."
        );
        if (!ok) return;
        try {
            setBusy(true);
            toast.info("AI is writing your month's plan — sit tight for ~45-60 seconds…");
            // 1. Kick off the background job. Endpoint returns immediately.
            await axios.post(
                `${API}/system-version/${draft.month}/auto-generate`,
                {
                    monthly_theme: simpleTheme.trim(),
                    visual_style_preset: simplePreset,
                    client_note: simpleNote.trim(),
                    custom_style_note: simpleCustomStyle.trim(),
                },
                { headers: studioHeaders(), timeout: 20000 }
            );
            // 2. Poll the version doc every 3s until the job flips done/failed.
            const startedAt = Date.now();
            const POLL_MS = 3000;
            const TIMEOUT_MS = 3 * 60 * 1000; // 3 min hard cap
            let consecutiveErrors = 0;
            while (true) {
                await new Promise((res) => setTimeout(res, POLL_MS));
                let r;
                try {
                    r = await axios.get(`${API}/system-version/${draft.month}`, { headers: studioHeaders() });
                    consecutiveErrors = 0;
                } catch {
                    // Swallow transient network blips — Claude may already be
                    // done in the background; just retry until TIMEOUT_MS.
                    consecutiveErrors += 1;
                    if (consecutiveErrors >= 5) {
                        toast.error("Lost connection while polling — refresh the page; your job may still finish in the background.");
                        break;
                    }
                    continue;
                }
                const job = r.data?.generation_job;
                if (job?.status === "done") {
                    setDraft(r.data);
                    toast.success("Done! Review the preview below, then click Publish.");
                    break;
                }
                if (job?.status === "failed") {
                    toast.error("AI generation failed: " + (job.error || "unknown error"));
                    setDraft(r.data);
                    break;
                }
                if (Date.now() - startedAt > TIMEOUT_MS) {
                    toast.error("Timed out waiting for AI — try again or check Studio later.");
                    break;
                }
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI generation failed");
        } finally { setBusy(false); }
    };

    const remove = async (month) => {
        if (!window.confirm(`Delete ${month}? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/system-version/${month}`, { headers: studioHeaders() });
            toast.success("Deleted");
            if (activeMonth === month) setActiveMonth(null);
            await load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    };

    const generateTrends = async () => {
        if (!draft) return;
        if (!window.confirm("Re-generate ONLY the 5 IG trends? (Hooks/styles/prompts unchanged.)")) return;
        try {
            setBusy(true);
            toast.info("Asking Claude for fresh trends — 15-30s…");
            const r = await axios.post(
                `${API}/system-version/${draft.month}/generate-trends-draft`,
                {}, { headers: studioHeaders(), timeout: 90000 }
            );
            setDraft({ ...draft, ig_trends: r.data.trends });
            toast.success(`Refreshed ${r.data.trends_count} trends`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "AI trend generation failed");
        } finally { setBusy(false); }
    };

    const hasGenerated = draft && (
        (draft.ig_trends && draft.ig_trends.length > 0) ||
        (draft.hook_structure && draft.hook_structure.length > 0) ||
        (draft.visual_styles && Object.keys(draft.visual_styles).length > 0)
    );

    return (
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6" data-testid="sysver-editor">
            {/* Left: month list */}
            <aside className="border-r border-border pr-5">
                <button
                    onClick={() => createMonth(nextMonthString())}
                    className="w-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream rounded-full px-4 py-2.5 text-xs font-bold uppercase tracking-[0.12em] flex items-center justify-center gap-2 mb-4"
                    disabled={busy}
                    data-testid="new-month-btn"
                >
                    <Plus className="h-4 w-4" /> New month
                </button>
                <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-mocha font-bold mb-2">Versions</p>
                <ul className="space-y-1">
                    {versions.map((v) => (
                        <li key={v.month}>
                            <button
                                onClick={() => setActiveMonth(v.month)}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-colors ${activeMonth === v.month ? "bg-cafe-olive/10 text-cafe-espresso font-medium" : "text-cafe-mocha hover:bg-cafe-cream"}`}
                                data-testid={`version-row-${v.month}`}
                            >
                                <span>{v.month}</span>
                                {v.published ? (
                                    <CheckCircle2 className="h-3.5 w-3.5 text-cafe-olive" />
                                ) : (
                                    <span className="text-[10px] uppercase tracking-wider text-cafe-terracotta">Draft</span>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            </aside>

            {/* Right: editor */}
            {!draft ? (
                <div className="text-cafe-mocha italic p-6">Select a version on the left, or create a new month.</div>
            ) : (
                <div className="space-y-6">
                    {/* Header */}
                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold">System Version</p>
                            <h2 className="font-serif text-2xl mt-0.5">{draft.month}</h2>
                            <p className="text-xs text-cafe-mocha mt-1">
                                {draft.published ? `✓ Published ${new Date(draft.published_at).toLocaleDateString()}` : "Draft — not yet live"}
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={publish} disabled={busy || draft.published || !hasGenerated} className="bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream rounded-full px-5 py-2 text-xs font-bold uppercase tracking-[0.12em] flex items-center gap-2 disabled:opacity-50" data-testid="publish-btn" title={!hasGenerated ? "Click Auto-Generate first" : ""}>
                                <Send className="h-3.5 w-3.5" /> {draft.published ? "Published" : "Publish"}
                            </button>
                            <button onClick={() => remove(draft.month)} disabled={busy} className="text-cafe-terracotta hover:bg-cafe-terracotta/10 rounded-full p-2" title="Delete">
                                <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {/* ─── SIMPLE MODE — the 3 inputs + big magic button ─── */}
                    <section className="bg-gradient-to-br from-cafe-cream/80 to-white border-2 border-cafe-olive/30 rounded-2xl p-6 shadow-sm" data-testid="simple-mode-card">
                        <div className="flex items-center gap-2 mb-1">
                            <Wand2 className="h-4 w-4 text-cafe-olive" />
                            <h3 className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold">Simple Mode · Just 3 things</h3>
                        </div>
                        <p className="text-xs text-cafe-mocha mb-5 italic">
                            Fill these three boxes, then click the big button. The AI will write everything else for you.
                        </p>

                        {/* Field 1: Theme */}
                        <div className="mb-5">
                            <label className="text-xs font-bold text-cafe-espresso block mb-1.5">
                                1 · This month&apos;s theme <span className="text-cafe-terracotta">*</span>
                            </label>
                            <input
                                value={simpleTheme}
                                onChange={(e) => setSimpleTheme(e.target.value)}
                                placeholder="e.g. Latte Art Showcase / Brunch Menu Launch / Festive Season Drinks"
                                className="w-full border border-border rounded-lg px-4 py-3 text-sm bg-white focus:border-cafe-olive focus:outline-none"
                                data-testid="simple-theme-input"
                            />
                            <p className="text-[11px] text-cafe-mocha mt-1.5">One sentence is enough. Example: &ldquo;Aiskrim Malaysia heritage flavours&rdquo;</p>
                        </div>

                        {/* Field 2: Style preset */}
                        <div className="mb-5">
                            <label className="text-xs font-bold text-cafe-espresso block mb-2">
                                2 · Visual style <span className="text-cafe-terracotta">*</span>
                            </label>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                {STYLE_PRESETS.map((s) => (
                                    <button
                                        key={s.value}
                                        type="button"
                                        onClick={() => setSimplePreset(s.value)}
                                        className={`text-left p-3 rounded-lg border-2 transition-all ${simplePreset === s.value
                                            ? "border-cafe-olive bg-cafe-olive/10"
                                            : "border-border bg-white hover:border-cafe-olive/40"
                                            }`}
                                        data-testid={`style-preset-${s.value}`}
                                    >
                                        <div className="text-xl mb-1">{s.emoji}</div>
                                        <div className="text-xs font-bold text-cafe-espresso">{s.label}</div>
                                        <div className="text-[10px] text-cafe-mocha mt-0.5 leading-tight">{s.hint}</div>
                                    </button>
                                ))}
                            </div>
                            {simplePreset === "custom" && (
                                <textarea
                                    value={simpleCustomStyle}
                                    onChange={(e) => setSimpleCustomStyle(e.target.value)}
                                    placeholder="Describe the look you want… e.g. 'Pastel cottagecore, lots of dried flowers, soft mist, hand-written labels'"
                                    rows={3}
                                    className="mt-2 w-full border border-border rounded-lg p-3 text-sm bg-white"
                                    data-testid="simple-custom-style-input"
                                />
                            )}
                        </div>

                        {/* Field 3: Client-facing note */}
                        <div className="mb-6">
                            <label className="text-xs font-bold text-cafe-espresso block mb-1.5">
                                3 · One-line note for your subscribers <span className="text-cafe-mocha font-normal">(optional)</span>
                            </label>
                            <input
                                value={simpleNote}
                                onChange={(e) => setSimpleNote(e.target.value)}
                                placeholder="e.g. This month we are leaning into festive flavours and warm tungsten lighting."
                                className="w-full border border-border rounded-lg px-4 py-3 text-sm bg-white focus:border-cafe-olive focus:outline-none"
                                maxLength={240}
                                data-testid="simple-note-input"
                            />
                            <p className="text-[11px] text-cafe-mocha mt-1.5">This is what subscribers see when they hover over the version badge.</p>
                        </div>

                        {/* Big magic button */}
                        <button
                            onClick={autoGenerate}
                            disabled={busy}
                            className="w-full bg-gradient-to-r from-cafe-olive via-cafe-oliveDark to-cafe-olive text-cafe-cream rounded-xl px-6 py-5 text-base font-bold uppercase tracking-[0.18em] flex items-center justify-center gap-3 hover:shadow-lg disabled:opacity-50 transition-all"
                            data-testid="auto-generate-btn"
                        >
                            <Sparkles className="h-5 w-5" />
                            {busy ? "Generating… ~45s" : (hasGenerated ? "Re-generate everything" : "Auto-Generate this month")}
                        </button>
                        {hasGenerated && (
                            <p className="text-[11px] text-cafe-mocha mt-3 text-center italic">
                                Already generated. Re-running will overwrite the current trends, hooks, and styles.
                            </p>
                        )}
                    </section>

                    {/* ─── PREVIEW — friendly summary of what the AI made ─── */}
                    {hasGenerated && (
                        <section className="bg-white border border-border rounded-2xl p-5" data-testid="preview-card">
                            <div className="flex items-center gap-2 mb-4">
                                <Eye className="h-4 w-4 text-cafe-olive" />
                                <h3 className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold">Preview · what subscribers will get</h3>
                            </div>

                            {draft.changelog && (
                                <div className="mb-5 p-3 bg-cafe-cream/40 rounded-lg border-l-2 border-cafe-olive">
                                    <p className="text-[10px] uppercase tracking-wider text-cafe-mocha font-bold mb-1">Subscriber note</p>
                                    <p className="text-sm text-cafe-espresso italic">&ldquo;{draft.changelog}&rdquo;</p>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                                <StatChip label="IG Trends" value={(draft.ig_trends || []).length} />
                                <StatChip label="Hook Formulas" value={(draft.hook_structure || []).length} />
                                <StatChip label="Visual Styles" value={Object.keys(draft.visual_styles || {}).length} />
                            </div>

                            {/* Top 3 trends preview */}
                            {(draft.ig_trends || []).length > 0 && (
                                <div className="mb-5">
                                    <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-mocha font-bold mb-2">Top 3 trends this month</p>
                                    <ul className="space-y-2">
                                        {(draft.ig_trends || []).slice(0, 3).map((t, i) => (
                                            <li key={i} className="text-sm border-l-2 border-cafe-olive/40 pl-3 py-1">
                                                <span className="font-bold text-cafe-espresso">{t.trend}</span>
                                                <span className="text-cafe-mocha"> — {t.why_it_works}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Top 3 hooks preview */}
                            {(draft.hook_structure || []).length > 0 && (
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-mocha font-bold mb-2">Sample hooks</p>
                                    <ul className="space-y-1.5">
                                        {(draft.hook_structure || []).slice(0, 3).map((h, i) => (
                                            <li key={i} className="text-sm text-cafe-espresso">
                                                <span className="text-[10px] uppercase tracking-wider text-cafe-olive font-bold mr-2">{h.category}</span>
                                                {h.example}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="flex gap-2 mt-6 pt-4 border-t border-border">
                                <button onClick={generateTrends} disabled={busy} className="bg-cafe-terracotta/90 hover:bg-cafe-terracotta text-cafe-cream rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] flex items-center gap-2" data-testid="generate-trends-btn">
                                    <Sparkles className="h-3.5 w-3.5" /> Refresh trends only
                                </button>
                            </div>
                        </section>
                    )}

                    {/* ─── ADVANCED — hidden behind accordion ─── */}
                    <section className="border border-dashed border-border rounded-2xl overflow-hidden">
                        <button
                            onClick={() => setShowAdvanced((v) => !v)}
                            className="w-full px-5 py-4 flex items-center justify-between hover:bg-cafe-cream/30 text-left"
                            data-testid="toggle-advanced-btn"
                        >
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.22em] text-cafe-mocha font-bold">Advanced Settings</p>
                                <p className="text-xs text-cafe-mocha mt-0.5 italic">
                                    Edit raw JSON, prompts, and per-field overrides. You almost never need this.
                                </p>
                            </div>
                            {showAdvanced ? <ChevronUp className="h-4 w-4 text-cafe-mocha" /> : <ChevronDown className="h-4 w-4 text-cafe-mocha" />}
                        </button>
                        {showAdvanced && (
                            <div className="p-5 border-t border-border space-y-6 bg-white">
                                <div className="flex justify-end">
                                    <button onClick={save} disabled={busy} className="bg-cafe-cream border border-border hover:border-cafe-olive text-cafe-espresso rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] flex items-center gap-2" data-testid="save-btn">
                                        <RefreshCcw className="h-3.5 w-3.5" /> Save raw edits
                                    </button>
                                </div>
                                <Section title="Changelog · raw text" testid="changelog-section">
                                    <textarea
                                        className="w-full border border-border rounded-lg p-3 text-sm bg-white"
                                        rows={3}
                                        value={draft.changelog || ""}
                                        onChange={(e) => setDraft({ ...draft, changelog: e.target.value })}
                                        data-testid="changelog-input"
                                    />
                                </Section>

                                <Section title="IG Trends" testid="trends-section">
                                    <p className="text-xs text-cafe-mocha mb-3 italic flex items-start gap-1.5">
                                        <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                                        AI drafts are creator-playbook based. Cross-check Meta Creator Hub before publishing.
                                    </p>
                                    <ListEditor
                                        items={draft.ig_trends || []}
                                        onChange={(arr) => setDraft({ ...draft, ig_trends: arr })}
                                        fields={[
                                            { key: "trend", label: "Trend name", placeholder: "e.g. POV: ordering at a quiet morning café" },
                                            { key: "why_it_works", label: "Why it works", multiline: true },
                                            { key: "how_to_use", label: "How to use", multiline: true },
                                            { key: "sample_caption", label: "Sample caption", multiline: true },
                                        ]}
                                        testidPrefix="trend"
                                    />
                                </Section>

                                <Section title="Hook Library" testid="hooks-section">
                                    <ListEditor
                                        items={draft.hook_structure || []}
                                        onChange={(arr) => setDraft({ ...draft, hook_structure: arr })}
                                        fields={[
                                            { key: "category", label: "Category", placeholder: "curiosity / list / story / question / controversial" },
                                            { key: "formula", label: "Formula" },
                                            { key: "char_limit", label: "Char limit", type: "number" },
                                            { key: "example", label: "Example" },
                                        ]}
                                        testidPrefix="hook"
                                    />
                                </Section>

                                <Section title="Reel Pacing" testid="pacing-section">
                                    <DictEditor obj={draft.reel_pacing || {}} onChange={(o) => setDraft({ ...draft, reel_pacing: o })} testidPrefix="pacing" />
                                </Section>

                                <Section title="Visual Styles (one_word → style anchor)" testid="styles-section">
                                    <DictEditor obj={draft.visual_styles || {}} onChange={(o) => setDraft({ ...draft, visual_styles: o })} multiline testidPrefix="style" />
                                </Section>

                                <Section title="AI Rendering Stack" testid="rendering-section">
                                    <DictEditor obj={draft.ai_rendering || {}} onChange={(o) => setDraft({ ...draft, ai_rendering: o })} testidPrefix="rendering" />
                                </Section>

                                <Section title="Prompts (advanced)" testid="prompts-section">
                                    <DictEditor obj={draft.prompts || {}} onChange={(o) => setDraft({ ...draft, prompts: o })} multiline testidPrefix="prompts" />
                                </Section>
                            </div>
                        )}
                    </section>
                </div>
            )}
        </div>
    );
}

function StatChip({ label, value }) {
    return (
        <div className="bg-cafe-cream/50 border border-border rounded-lg p-3 text-center">
            <div className="font-serif text-3xl text-cafe-espresso">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-cafe-mocha font-bold mt-1">{label}</div>
        </div>
    );
}

function Section({ title, children, cta, testid }) {
    return (
        <section className="bg-white border border-border rounded-2xl p-5" data-testid={testid}>
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-[10px] uppercase tracking-[0.22em] text-cafe-olive font-bold">{title}</h3>
                {cta}
            </div>
            {children}
        </section>
    );
}

function ListEditor({ items, onChange, fields, testidPrefix }) {
    const add = () => onChange([...items, Object.fromEntries(fields.map((f) => [f.key, f.type === "number" ? 0 : ""]))]);
    const update = (i, key, val) => {
        const next = [...items];
        next[i] = { ...next[i], [key]: val };
        onChange(next);
    };
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

    return (
        <div className="space-y-3">
            {items.map((it, i) => (
                <div key={i} className="border border-border rounded-lg p-3 bg-cafe-cream/40" data-testid={`${testidPrefix}-row-${i}`}>
                    <div className="flex justify-end mb-2">
                        <button onClick={() => remove(i)} className="text-cafe-terracotta text-xs">remove</button>
                    </div>
                    {fields.map((f) => (
                        <div key={f.key} className="mb-2 last:mb-0">
                            <label className="text-[10px] uppercase tracking-wider text-cafe-mocha font-bold block mb-1">{f.label}</label>
                            {f.multiline ? (
                                <textarea
                                    className="w-full border border-border rounded p-2 text-sm bg-white"
                                    rows={2}
                                    value={it[f.key] ?? ""}
                                    onChange={(e) => update(i, f.key, e.target.value)}
                                    placeholder={f.placeholder}
                                />
                            ) : (
                                <input
                                    type={f.type || "text"}
                                    className="w-full border border-border rounded p-2 text-sm bg-white"
                                    value={it[f.key] ?? ""}
                                    onChange={(e) => update(i, f.key, f.type === "number" ? Number(e.target.value) : e.target.value)}
                                    placeholder={f.placeholder}
                                />
                            )}
                        </div>
                    ))}
                </div>
            ))}
            <button onClick={add} className="text-cafe-olive text-xs font-bold uppercase tracking-wider flex items-center gap-1" data-testid={`${testidPrefix}-add-btn`}>
                <Plus className="h-3 w-3" /> Add
            </button>
        </div>
    );
}

function DictEditor({ obj, onChange, multiline, testidPrefix }) {
    const entries = Object.entries(obj || {});
    const updateKey = (oldKey, newKey) => {
        if (!newKey || newKey === oldKey) return;
        const next = {};
        for (const [k, v] of entries) next[k === oldKey ? newKey : k] = v;
        onChange(next);
    };
    const updateVal = (key, val) => onChange({ ...obj, [key]: val });
    const remove = (key) => {
        const next = { ...obj };
        delete next[key];
        onChange(next);
    };
    const add = () => {
        let i = 1;
        while (obj[`new_key_${i}`] !== undefined) i++;
        onChange({ ...obj, [`new_key_${i}`]: "" });
    };
    return (
        <div className="space-y-2">
            {entries.map(([k, v]) => (
                <div key={k} className="grid grid-cols-[160px_1fr_auto] gap-2 items-start" data-testid={`${testidPrefix}-row-${k}`}>
                    <input
                        className="border border-border rounded px-2 py-1.5 text-xs font-mono bg-white"
                        defaultValue={k}
                        onBlur={(e) => updateKey(k, e.target.value)}
                    />
                    {multiline || (typeof v === "string" && v.length > 60) ? (
                        <textarea
                            className="border border-border rounded px-2 py-1.5 text-sm bg-white"
                            rows={Math.max(2, Math.ceil((v?.length || 0) / 60))}
                            value={typeof v === "object" ? JSON.stringify(v) : (v ?? "")}
                            onChange={(e) => updateVal(k, e.target.value)}
                        />
                    ) : (
                        <input
                            type={typeof v === "number" ? "number" : "text"}
                            className="border border-border rounded px-2 py-1.5 text-sm bg-white"
                            value={typeof v === "object" ? JSON.stringify(v) : (v ?? "")}
                            onChange={(e) => updateVal(k, typeof v === "number" ? Number(e.target.value) : e.target.value)}
                        />
                    )}
                    <button onClick={() => remove(k)} className="text-cafe-terracotta hover:bg-cafe-terracotta/10 rounded p-1.5" title="Remove">
                        <Trash2 className="h-3.5 w-3.5" />
                    </button>
                </div>
            ))}
            <button onClick={add} className="text-cafe-olive text-xs font-bold uppercase tracking-wider flex items-center gap-1" data-testid={`${testidPrefix}-add-btn`}>
                <Plus className="h-3 w-3" /> Add field
            </button>
        </div>
    );
}
